import { createFileRoute } from "@tanstack/react-router";
import { CalendarClock, CircleHelp, Eraser, Pause, Play, Radio, Search, ShieldCheck, SearchX, X } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useEnvName, useProjectSlug } from "@/app/context";
import { useRegisterCommands } from "@/app/commands";
import {
  Button,
  EmptyState,
  ErrorState,
  ExplainPanel,
  Input,
  Kbd,
  Popover,
  PopoverClose,
  PopoverContent,
  PopoverTrigger,
  Segmented,
  Select,
  ShowAsCode,
  StatusDot,
  Tooltip,
} from "@/design";
import { useLogs, useLogTail, useServiceInstances, type LogFilter, type LogLine } from "@/lib/api";
import { cn } from "@/lib/cn";
import { docsUrl } from "@/lib/config";
import { formatDateTime, formatNumber } from "@/lib/format";
import { seriesToken } from "./-charts/colors";
import { logTime, rowKey } from "./-logs/LogRow";
import { LogViewer } from "./-logs/LogViewer";
import { cliFor, EXAMPLES, matchLine, parseQuery, removeToken, serverFilter, setToken, type Token } from "./-logs/query";
import { VolumeStrip } from "./-logs/VolumeStrip";
import { prefetchInstances, prefetchLogs } from "./-pending/prefetch";
import { LogsPending, LogsSkeleton } from "./-pending/skeletons";

type RangeKey = "live" | "15m" | "1h" | "6h" | "24h" | "7d";
const RANGES: { value: RangeKey; label: string; long: string }[] = [
  { value: "live", label: "Live", long: "live" },
  { value: "15m", label: "15m", long: "the last 15 minutes" },
  { value: "1h", label: "1h", long: "the last hour" },
  { value: "6h", label: "6h", long: "the last 6 hours" },
  { value: "24h", label: "24h", long: "the last 24 hours" },
  { value: "7d", label: "7d", long: "the last 7 days" },
];

interface LogsSearch {
  q?: string;
  range?: RangeKey;
  /** ISO time to centre on ("jump to time"). */
  at?: string;
}

export const Route = createFileRoute("/$workspace/$project/logs")({
  staticData: { title: "Logs" },
  validateSearch: (s: Record<string, unknown>): LogsSearch => ({
    ...(typeof s.q === "string" && s.q ? { q: s.q } : {}),
    ...(typeof s.range === "string" && RANGES.some((r) => r.value === s.range) ? { range: s.range as RangeKey } : {}),
    ...(typeof s.at === "string" && !Number.isNaN(Date.parse(s.at)) ? { at: s.at } : {}),
  }),
  // Start the service list and the newest lines while the screen's code downloads (and on hover).
  loaderDeps: ({ search }) => ({ env: (search as { env?: string }).env ?? "production", q: search.q, range: search.range, at: search.at }),
  loader: ({ context, params, deps }) => {
    prefetchInstances(context.queryClient, params.project, deps.env);
    const r = logRequests(deps, deps.env);
    prefetchLogs(context.queryClient, params.project, r.live ? r.recent : r.history);
  },
  pendingComponent: LogsPending,
  pendingMs: 150,
  pendingMinMs: 200,
  component: LogsScreen,
});

/**
 * The log queries a screen state needs. The loader and the screen share it, so they hit the same cache.
 * Live mode first shows the last 15 minutes, then appends the tail: a quiet app still shows its logs at once.
 */
function logRequests(search: LogsSearch, env: string) {
  const at = search.at;
  const range: RangeKey = at ? (search.range && search.range !== "live" ? search.range : "1h") : (search.range ?? "live");
  const live = range === "live" && !at;
  const filter = serverFilter(parseQuery(search.q ?? ""));
  const t = at ? Date.parse(at) : 0;
  const window = at ? { since: new Date(t - 10 * 60_000).toISOString(), until: new Date(t + 10 * 60_000).toISOString() } : { since: range };
  return {
    live,
    recent: { environment: env, ...filter, since: "15m", limit: 200 } as LogFilter,
    history: { environment: env, ...filter, ...window, limit: 500 } as LogFilter,
  };
}

const LEVEL_OPTIONS = [
  { value: "all", label: "All" },
  { value: "info", label: "Info+" , title: "Info, warnings and errors (hides debug)" },
  { value: "warn", label: "Warn+", title: "Warnings and errors" },
  { value: "error", label: "Errors", title: "Errors and fatal errors only" },
] as const;

function dedupe(lines: LogLine[]): LogLine[] {
  const seen = new Set<string>();
  const out: LogLine[] = [];
  for (const l of lines) {
    const k = rowKey(l);
    if (seen.has(k)) continue;
    seen.add(k);
    out.push(l);
  }
  out.sort((a, b) => Date.parse(a.timestamp) - Date.parse(b.timestamp));
  return out;
}

function LogsScreen() {
  const project = useProjectSlug();
  const env = useEnvName();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const applied = search.q ?? "";
  const at = search.at;
  const range: RangeKey = at ? (search.range && search.range !== "live" ? search.range : "1h") : (search.range ?? "live");
  const live = range === "live" && !at;

  const [draft, setDraft] = useState(applied);
  const narrow = useNarrow();
  const inputRef = useRef<HTMLInputElement>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [follow, setFollow] = useState(true);
  const [snapshot, setSnapshot] = useState<LogLine[] | null>(null);
  const followStopCount = useRef(0);
  const [scrollRequest, setScrollRequest] = useState(0);
  const [pickedIndex, setPickedIndex] = useState<number | null>(null);

  const setSearch = useCallback(
    (patch: Partial<LogsSearch>) => void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true }),
    [navigate],
  );

  // Keep the box in sync with the URL (back button, chips, controls).
  useEffect(() => setDraft(applied), [applied]);
  // Debounced apply while typing.
  useEffect(() => {
    if (draft === applied) return;
    const t = setTimeout(() => setSearch({ q: draft.trim() || undefined }), 400);
    return () => clearTimeout(t);
  }, [draft, applied, setSearch]);

  const parsed = useMemo(() => parseQuery(applied), [applied]);
  const filter = useMemo(() => serverFilter(parsed), [parsed]);

  const instances = useServiceInstances(project, env);
  const serviceNames = useMemo(() => (instances.data?.data ?? []).map((i) => i.name), [instances.data]);
  const serviceColor = useCallback(
    (name: string | undefined) => {
      const i = name ? serviceNames.indexOf(name) : -1;
      return i < 0 ? "--text-subtle" : seriesToken(i);
    },
    [serviceNames],
  );

  const requests = useMemo(() => logRequests(search, env), [search, env]);

  /* Live tail, on top of the last 15 minutes so the screen is never blank while the app is quiet */
  const recent = useLogs(live ? project : "", requests.recent);
  const tail = useLogTail(project, { environment: env, ...filter }, { enabled: live, max: 3000 });
  const liveLines = useMemo(() => dedupe([...(recent.data?.lines ?? []), ...tail.lines]).filter((l) => matchLine(l, parsed)), [recent.data, tail.lines, parsed]);

  /* History */
  const history = useLogs(live ? "" : project, requests.history);
  const historyLines = useMemo(() => (history.data ? dedupe(history.data.lines).filter((l) => matchLine(l, parsed)) : []), [history.data, parsed]);

  const paused = live && snapshot != null;
  const lines = live ? (snapshot ?? liveLines) : historyLines;

  // Anchor: the first line at or after the jump time.
  const anchorIndex = useMemo(() => {
    if (pickedIndex != null) return pickedIndex;
    if (!at || live) return null;
    const t = Date.parse(at);
    const i = lines.findIndex((l) => Date.parse(l.timestamp) >= t);
    return i < 0 ? lines.length - 1 : i;
  }, [at, live, lines, pickedIndex]);

  useEffect(() => {
    if (!live && at && history.data) {
      setFollow(false);
      setScrollRequest((n) => n + 1);
    } else if (!live && history.data) {
      setFollow(true);
    }
  }, [live, at, history.data]);

  useEffect(() => {
    setPickedIndex(null);
  }, [applied, range, at]);

  const unseen = paused ? Math.max(0, liveLines.length - (snapshot?.length ?? 0)) : !follow && live ? Math.max(0, liveLines.length - followStopCount.current) : 0;

  const onFollowChange = (f: boolean) => {
    if (!f) followStopCount.current = liveLines.length;
    setFollow(f);
    if (f) setPickedIndex(null);
  };

  const togglePause = () => {
    if (!live) return;
    if (paused) {
      setSnapshot(null);
      setFollow(true);
    } else {
      setSnapshot(liveLines);
    }
  };

  const onToggle = useCallback((key: string) => {
    setExpanded((s) => {
      const n = new Set(s);
      if (n.has(key)) n.delete(key);
      else n.add(key);
      return n;
    });
  }, []);

  const applyNow = (q: string) => {
    setDraft(q);
    setSearch({ q: q.trim() || undefined });
  };
  const addToken = useCallback(
    (token: string) => {
      const [k, ...rest] = token.split(":");
      const v = rest.join(":");
      const next = k === "service" || k === "level" || k === "source" ? setToken(applied, k, v) : `${applied} ${token}`.trim();
      setDraft(next);
      setSearch({ q: next || undefined });
    },
    [applied, setSearch],
  );
  const jumpTo = useCallback((iso: string) => setSearch({ at: iso, range: undefined }), [setSearch]);

  // "/" focuses search from anywhere on the page.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      if (e.key !== "/" || e.metaKey || e.ctrlKey || e.altKey) return;
      if (t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.isContentEditable)) return;
      e.preventDefault();
      inputRef.current?.focus();
      inputRef.current?.select();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useRegisterCommands(
    "Logs",
    [
      ...(live ? [{ id: "logs-pause", label: paused ? "Resume live tail" : "Pause live tail", icon: paused ? <Play /> : <Pause />, run: togglePause }] : [{ id: "logs-live", label: "Back to live logs", icon: <Radio />, run: () => setSearch({ range: undefined, at: undefined }) }]),
      { id: "logs-errors", label: "Show only errors", keywords: ["level", "error"], run: () => addToken("level:error") },
      { id: "logs-clear", label: "Clear log filters", icon: <Eraser />, run: () => applyNow("") },
      { id: "logs-search", label: "Search logs", icon: <Search />, hint: <Kbd>/</Kbd>, run: () => inputRef.current?.focus() },
    ],
    [live, paused, applied],
  );

  const counts = useMemo(() => {
    let e = 0;
    let w = 0;
    for (const l of lines) {
      if (l.level === "error" || l.level === "fatal") e++;
      else if (l.level === "warn") w++;
    }
    return { e, w };
  }, [lines]);

  const levelValue = parsed.level === "info" || parsed.level === "warn" || parsed.level === "error" ? parsed.level : "all";
  const rangeLong = at ? `the 20 minutes around ${formatDateTime(at)}` : (RANGES.find((r) => r.value === range)?.long ?? "");
  const loading = live ? tail.lines.length === 0 && !tail.error && (recent.isPending || (!recent.data && !tail.connected)) : history.isPending;
  const error = live ? (tail.error && !tail.connected && tail.lines.length === 0 && !recent.data ? tail.error : null) : history.error;
  const hasFilters = parsed.tokens.length > 0;

  return (
    <div className="flex h-[calc(100dvh-3.5rem)] min-h-[560px] flex-col">
      {/* Header */}
      <div className="px-4 pt-6 sm:px-6 lg:px-10 lg:pt-8">
        <header className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div className="min-w-0">
            <h1 className="font-display text-3xl font-semibold tracking-tight text-text">Logs</h1>
            <p className="mt-1.5 max-w-2xl text-md text-muted">Everything your services print, plus every request they answer, in one place.</p>
          </div>
          <div className="flex shrink-0 flex-wrap items-center gap-2">
            <ShowAsCode
              title="Read these logs from your terminal or agent"
              cli={cliFor(parsed, env, live, at ? undefined : range)}
              apiCall={{ method: "GET", path: `/v1/projects/${project}/logs${live ? "/tail" : ""}?environment=${env}${filter.service ? `&service=${filter.service}` : ""}${filter.level ? `&level=${filter.level}` : ""}${filter.q ? `&q=${encodeURIComponent(filter.q)}` : ""}` }}
            />
            {live ? (
              <Button variant={paused ? "primary" : "secondary"} icon={paused ? <Play /> : <Pause />} onClick={togglePause} aria-pressed={paused}>
                {paused ? "Resume" : "Pause"}
              </Button>
            ) : (
              <Button variant="primary" icon={<Radio />} onClick={() => setSearch({ range: undefined, at: undefined })}>
                Back to live
              </Button>
            )}
          </div>
        </header>

        {/* Search */}
        <form
          role="search"
          onSubmit={(e) => {
            e.preventDefault();
            applyNow(draft);
          }}
        >
          <label htmlFor="log-search" className="sr-only">
            Search logs
          </label>
          <Input
            ref={inputRef}
            id="log-search"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Escape") {
                if (draft) {
                  e.preventDefault();
                  applyNow("");
                } else inputRef.current?.blur();
              }
            }}
            placeholder={narrow ? "Search, or try level:error" : 'Search, or filter like  service:api level:error status:5xx -healthz "timeout"'}
            autoComplete="off"
            spellCheck={false}
            leading={<Search />}
            className="[&_input]:h-10 [&_input]:pr-24 [&_input]:font-mono [&_input]:text-sm sm:[&_input]:pr-28"
            aria-describedby="log-search-help"
            trailing={
              <span className="flex items-center gap-1">
                {draft && (
                  <button type="button" onClick={() => applyNow("")} className="flex size-7 items-center justify-center rounded-md text-subtle hover:bg-surface-2 hover:text-text" aria-label="Clear search">
                    <X className="size-4" />
                  </button>
                )}
                <Kbd className="mr-1 hidden sm:inline-flex">/</Kbd>
                <SearchHelp onPick={applyNow} />
              </span>
            }
          />
          <p id="log-search-help" className="sr-only">
            Type words to find, or filters: service, level, source, status, path and method, for example level:error. Start a word with a minus sign to leave it out.
          </p>
        </form>

        {/* Parsed filters */}
        {hasFilters && (
          <ul className="mt-2 flex flex-wrap items-center gap-1.5" aria-label="Active filters">
            {parsed.tokens.map((t, i) => (
              <li key={`${t.raw}-${i}`}>
                <TokenChip token={t} onRemove={() => applyNow(removeToken(applied, t.raw))} />
              </li>
            ))}
            <li>
              <button type="button" onClick={() => applyNow("")} className="ml-1 text-xs font-medium text-muted underline-offset-4 hover:text-text hover:underline">
                Clear all
              </button>
            </li>
          </ul>
        )}

        {/* Controls */}
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <div className="w-[calc(50%-0.25rem)] sm:w-44">
            <Select
              size="sm"
              aria-label="Service"
              value={parsed.service ?? "__all"}
              onValueChange={(v) => applyNow(setToken(applied, "service", v === "__all" ? null : v))}
              options={[{ value: "__all", label: "All services" }, ...serviceNames.map((n) => ({ value: n, label: n }))]}
            />
          </div>
          <Segmented
            aria-label="Minimum level"
            value={levelValue}
            onValueChange={(v) => applyNow(setToken(applied, "level", v === "all" ? null : v))}
            options={LEVEL_OPTIONS.map((o) => ({ ...o }))}
          />
          <div className="flex w-full flex-wrap items-center gap-2 sm:ml-auto sm:w-auto">
            <Segmented
              aria-label="Time range"
              value={at ? ("" as RangeKey) : range}
              onValueChange={(v) => {
                setSnapshot(null);
                setFollow(true);
                setSearch({ range: v === "live" ? undefined : v, at: undefined });
              }}
              options={RANGES.map((r) => ({
                value: r.value,
                label: r.value === "live" ? (
                  <span className="inline-flex items-center gap-1.5">
                    <span className={cn("size-1.5 rounded-full", live && !paused ? "bg-ok" : "bg-subtle")} aria-hidden="true" /> Live
                  </span>
                ) : (
                  r.label
                ),
                title: r.value === "live" ? "Stream new lines as they happen" : `Search ${r.long}`,
              }))}
            />
            <JumpToTime at={at} onJump={jumpTo} />
          </div>
        </div>
      </div>

      {/* Viewer */}
      <div className="mx-0 mt-4 flex min-h-0 flex-1 flex-col border-y border-border bg-surface sm:mx-6 sm:mb-6 sm:rounded-xl sm:border sm:shadow-xs lg:mx-10">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 border-b border-border px-3 pb-1 pt-2.5 sm:px-4">
          <div className="flex items-center gap-3 text-sm">
            <span className="tabular text-muted">
              <span className="font-medium text-text">{formatNumber(lines.length)}</span> {lines.length === 1 ? "line" : "lines"}
            </span>
            <button
              type="button"
              onClick={() => addToken("level:error")}
              disabled={!counts.e}
              className="tabular inline-flex items-center gap-1.5 rounded-sm text-muted hover:text-text disabled:pointer-events-none"
              title="Show only errors"
            >
              <span className="size-2 rounded-full bg-danger" aria-hidden="true" />
              {formatNumber(counts.e)} {counts.e === 1 ? "error" : "errors"}
            </button>
            <button
              type="button"
              onClick={() => addToken("level:warn")}
              disabled={!counts.w}
              className="tabular inline-flex items-center gap-1.5 rounded-sm text-muted hover:text-text disabled:pointer-events-none"
              title="Show warnings and errors"
            >
              <span className="size-2 rounded-full bg-warn" aria-hidden="true" />
              {formatNumber(counts.w)} {counts.w === 1 ? "warning" : "warnings"}
            </button>
          </div>
          <VolumeStrip
            lines={lines}
            className="min-w-0 flex-1 basis-full sm:basis-0"
            onPick={(i) => {
              onFollowChange(false);
              setPickedIndex(i);
              setScrollRequest((n) => n + 1);
            }}
          />
        </div>

        {/* Column headings (desktop) */}
        <div className="hidden grid-cols-[6.5rem_3.25rem_8rem_1fr] gap-x-3 border-b border-border bg-surface-2/60 px-4 py-1.5 text-2xs font-medium uppercase tracking-[0.06em] text-subtle sm:grid" aria-hidden="true">
          <span>Time (UK)</span>
          <span>Level</span>
          <span>Service</span>
          <span>Message</span>
        </div>

        {error ? (
          <div className="p-4">
            <ErrorState error={error} onRetry={live ? undefined : () => void history.refetch()} />
          </div>
        ) : loading ? (
          <LogsSkeleton />
        ) : (
          <LogViewer
            label={live ? "Live log lines, newest at the bottom" : `Log lines from ${rangeLong}`}
            lines={lines}
            terms={parsed.text}
            expanded={expanded}
            onToggle={onToggle}
            serviceColor={serviceColor}
            follow={follow && !paused}
            onFollowChange={onFollowChange}
            unseen={unseen}
            anchorIndex={anchorIndex}
            scrollRequest={scrollRequest}
            onJumpTo={jumpTo}
            onFilter={addToken}
            empty={
              <EmptyState
                icon={hasFilters ? <SearchX /> : <Radio />}
                title={hasFilters ? "No lines match" : live ? "Waiting for your app to say something" : "No logs in this range"}
                description={
                  hasFilters
                    ? `Nothing in ${live ? "the live stream so far" : rangeLong} matches ${applied}. Try fewer filters or a longer time range.`
                    : live
                      ? "New lines appear here the moment your services print them. Sleeping services wake on their next request."
                      : `Your services did not log anything in ${rangeLong}.`
                }
                actions={
                  hasFilters ? (
                    <Button size="sm" icon={<Eraser />} onClick={() => applyNow("")}>
                      Clear filters
                    </Button>
                  ) : !live ? (
                    <Button size="sm" onClick={() => setSearch({ range: "24h", at: undefined })}>
                      Search the last 24 hours
                    </Button>
                  ) : undefined
                }
              />
            }
          />
        )}

        {/* Status bar */}
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 border-t border-border px-3 py-2 text-xs text-muted sm:px-4" role="status" aria-live="polite">
          {live ? (
            paused ? (
              <StatusDot tone="warn" label={`Paused${unseen ? ` · ${formatNumber(unseen)} new ${unseen === 1 ? "line" : "lines"} waiting` : ""}`} explain="The list is frozen so you can read. New lines keep arriving in the background; press Resume to see them." />
            ) : tail.connected ? (
              <StatusDot tone="ok" pulse label="Live" explain="Connected. New lines appear within a second of your app printing them." />
            ) : !tail.error && !tail.lines.length ? (
              <StatusDot tone="idle" pulse label="Connecting…" explain="Opening the live stream. The lines above are from the last 15 minutes." />
            ) : (
              <StatusDot tone="warn" pulse label="Reconnecting…" explain="The live connection dropped. We reconnect automatically and pick up where we left off." />
            )
          ) : (
            <span className="inline-flex items-center gap-1.5">
              <CalendarClock className="size-3.5 text-subtle" />
              {lines.length ? (
                <>
                  {logTime(lines[0]!.timestamp).slice(0, 8)} to {logTime(lines[lines.length - 1]!.timestamp).slice(0, 8)}
                  {history.data?.has_more && " · showing the newest 500, narrow your search to see more"}
                </>
              ) : (
                rangeLong
              )}
            </span>
          )}
          {tail.dropped > 0 && live && (
            <span className="inline-flex items-center gap-1 text-warn-text">
              {formatNumber(tail.dropped)} lines skipped
              <ExplainPanel title="Why were lines skipped?" side="top">
                <p>Your app printed lines faster than we can stream them to a browser, so we skipped some in this live view. Nothing is lost: every line is stored and searchable.</p>
              </ExplainPanel>
            </span>
          )}
          <span className="ml-auto inline-flex items-center gap-1">
            <ShieldCheck className="size-3.5 text-subtle" />
            Shown as plain text
            <ExplainPanel title="Why plain text?" learnMoreUrl={docsUrl("/logs#untrusted")} side="top">
              <p>
                Log lines are written by your app, and sometimes by your visitors (a search box, a user agent). We never run them, turn them into links, or treat them as instructions,
                including when Ask Guv reads them.
              </p>
              <p>Secrets we recognise are hidden before they are stored and shown as a blue chip with the secret's name.</p>
            </ExplainPanel>
          </span>
          <span className="hidden items-center gap-1 lg:inline-flex">
            <Kbd>j</Kbd>
            <Kbd>k</Kbd> move · <Kbd>Enter</Kbd> details
          </span>
        </div>
      </div>
    </div>
  );
}

function TokenChip({ token, onRemove }: { token: Token; onRemove: () => void }) {
  const label =
    token.kind === "text" ? (
      <>“{token.value}”</>
    ) : token.kind === "exclude" ? (
      <>
        <span className="text-subtle">not</span> “{token.value}”
      </>
    ) : token.kind === "invalid" ? (
      <>{token.raw}</>
    ) : (
      <>
        <span className="text-subtle">{token.kind}</span> {token.kind === "level" ? `${token.value} and worse` : token.value}
      </>
    );
  const chip = (
    <span
      className={cn(
        "inline-flex h-6 items-center gap-1 rounded-full border pl-2.5 pr-1 font-mono text-xs",
        token.kind === "invalid" ? "border-danger/40 bg-danger-soft text-danger-text" : token.kind === "exclude" ? "border-border bg-surface-2 text-muted" : "border-accent/25 bg-accent-soft text-accent-text",
      )}
    >
      {label}
      <button type="button" onClick={onRemove} className="flex size-[18px] items-center justify-center rounded-full hover:bg-surface-3" aria-label={`Remove filter ${token.raw}`}>
        <X className="size-3" />
      </button>
    </span>
  );
  if (token.kind === "invalid" && token.error) return <Tooltip content={`${token.error} This part is ignored.`}>{chip}</Tooltip>;
  return chip;
}

function SearchHelp({ onPick }: { onPick: (q: string) => void }) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button type="button" className="flex size-7 items-center justify-center rounded-md text-subtle hover:bg-surface-2 hover:text-text" aria-label="Search tips">
          <CircleHelp className="size-4" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[min(26rem,calc(100vw-2rem))] p-0">
        <div className="px-4 pb-2 pt-4">
          <h2 className="text-md font-semibold text-text">Search tips</h2>
          <p className="mt-1 text-sm text-muted">Type words to find them anywhere in a line. Add filters to narrow things down. Everything combines.</p>
        </div>
        <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 px-4 py-2 text-sm">
          {[
            ["service:api", "Only the api service"],
            ["level:warn", "Warnings and worse"],
            ["status:5xx", "Failed requests (also 404, >=400)"],
            ["path:/api", "Request paths starting /api"],
            ["method:POST", "Only POST requests"],
            ["source:http", "Requests, or runtime, build, deploy"],
            ["-healthz", "Leave out lines with this word"],
            ['"two words"', "An exact phrase"],
          ].map(([k, v]) => (
            <div key={k} className="contents">
              <dt>
                <code className="rounded bg-surface-2 px-1.5 py-0.5 font-mono text-xs text-text">{k}</code>
              </dt>
              <dd className="text-muted">{v}</dd>
            </div>
          ))}
        </dl>
        <div className="border-t border-border bg-surface-2/60 px-4 py-3">
          <div className="mb-1.5 text-2xs font-semibold uppercase tracking-[0.08em] text-subtle">Try one</div>
          <ul className="space-y-1">
            {EXAMPLES.map((ex) => (
              <li key={ex.query}>
                <PopoverClose asChild>
                  <button type="button" onClick={() => onPick(ex.query)} className="group flex w-full flex-col rounded-md px-2 py-1.5 text-left hover:bg-surface">
                    <code className="font-mono text-xs text-accent-text">{ex.query}</code>
                    <span className="text-xs text-muted">{ex.meaning}</span>
                  </button>
                </PopoverClose>
              </li>
            ))}
          </ul>
        </div>
      </PopoverContent>
    </Popover>
  );
}

/** datetime-local works in the browser's time zone; we label it so nobody is surprised. */
function toLocalInput(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function JumpToTime({ at, onJump }: { at?: string; onJump: (iso: string) => void }) {
  const [value, setValue] = useState(() => toLocalInput(at ? new Date(at) : new Date(Date.now() - 15 * 60_000)));
  const [open, setOpen] = useState(false);
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const quick = [
    { label: "5 minutes ago", ms: 5 * 60_000 },
    { label: "30 minutes ago", ms: 30 * 60_000 },
    { label: "2 hours ago", ms: 2 * 3600_000 },
    { label: "Yesterday, same time", ms: 86400_000 },
  ];
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button size="sm" variant={at ? "subtle" : "secondary"} icon={<CalendarClock />} className="h-7">
          {at ? logTime(at).slice(0, 5) : (
            <>
              Jump<span className="hidden sm:inline">&nbsp;to time</span>
            </>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-72">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            const t = new Date(value);
            if (!Number.isNaN(t.getTime())) {
              onJump(t.toISOString());
              setOpen(false);
            }
          }}
          className="space-y-3"
        >
          <div>
            <label htmlFor="jump-time" className="text-sm font-medium text-text">
              Show logs around
            </label>
            <Input id="jump-time" type="datetime-local" value={value} max={toLocalInput(new Date())} onChange={(e) => setValue(e.target.value)} className="mt-1.5" />
            <p className="mt-1 text-xs text-muted">In your time zone ({tz}). We show 10 minutes either side.</p>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {quick.map((q) => (
              <button
                key={q.label}
                type="button"
                onClick={() => {
                  onJump(new Date(Date.now() - q.ms).toISOString());
                  setOpen(false);
                }}
                className="rounded-full border border-border px-2.5 py-1 text-xs text-muted hover:border-border-strong hover:text-text"
              >
                {q.label}
              </button>
            ))}
          </div>
          <Button type="submit" variant="primary" size="sm" className="w-full">
            Go to this time
          </Button>
        </form>
      </PopoverContent>
    </Popover>
  );
}

function useNarrow() {
  const query = "(max-width: 639px)";
  const [narrow, setNarrow] = useState(() => typeof window !== "undefined" && window.matchMedia(query).matches);
  useEffect(() => {
    const m = window.matchMedia(query);
    const on = () => setNarrow(m.matches);
    m.addEventListener("change", on);
    return () => m.removeEventListener("change", on);
  }, []);
  return narrow;
}
